import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Observable, Subject, Subscription, catchError, forkJoin, map, of, switchMap } from 'rxjs';
import Map from 'ol/Map';
import Feature from 'ol/Feature';
import Overlay from 'ol/Overlay';
import Point from 'ol/geom/Point';
import LineString from 'ol/geom/LineString';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import GeoJSON from 'ol/format/GeoJSON';
import { Circle as CircleStyle, Fill, Icon, Stroke, Style } from 'ol/style';
import { fromLonLat, toLonLat, transformExtent } from 'ol/proj';
import { unByKey } from 'ol/Observable';
import type { EventsKey } from 'ol/events';
import type { CollectionEvent } from 'ol/Collection';
import {
  DoubleClickZoom,
  DragPan,
  DragRotate,
  Interaction,
  KeyboardPan,
  KeyboardZoom,
  MouseWheelZoom,
  PinchRotate,
  PinchZoom,
  Translate,
} from 'ol/interaction';
import { GeoPlace, GeoRoute, GeoRouteProfile, GeoSearchService } from 'src/app/shared/services/geo-search.service';
import { LrsRoute, LrsService } from 'src/app/shared/services/lrs.service';

export interface GeoResultGroup {
  title: string;
  items: GeoPlace[];
}

type Endpoint = 'from' | 'to';

interface EndpointState {
  text: string;
  place: GeoPlace | null;
  groups: GeoResultGroup[];
  loading: boolean;
  error: string;
}

const PIN_COLORS = { pin: '#2563eb', from: '#16a34a', to: '#dc2626' } as const;
const NAV_INTERACTIONS = [DragPan, MouseWheelZoom, PinchZoom, PinchRotate, DragRotate, KeyboardPan, KeyboardZoom, DoubleClickZoom];

/**
 * The editor runs many unfiltered forEachFeatureAtPixel handlers (hover info, split, junction tools).
 * Only features flagged `gisOverlay` (the draggable From/To markers) are hit-detectable on this layer.
 */
class OverlayVectorLayer extends VectorLayer<VectorSource> {
  override createRenderer() {
    const renderer = super.createRenderer();
    const hit = renderer.forEachFeatureAtCoordinate.bind(renderer);
    renderer.forEachFeatureAtCoordinate = ((coordinate, frameState, hitTolerance, callback, matches) =>
      hit(
        coordinate,
        frameState,
        hitTolerance,
        (feature, layer, geometry) => (feature.get('gisOverlay') ? callback(feature, layer, geometry) : undefined),
        matches
      )) as typeof renderer.forEachFeatureAtCoordinate;
    return renderer;
  }
}

function pinIcon(color: string): Icon {
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="40" viewBox="0 0 28 40">` +
    `<path d="M14 1C6.8 1 1 6.7 1 13.9 1 23.7 14 39 14 39s13-15.3 13-25.1C27 6.7 21.2 1 14 1z" fill="${color}" stroke="#ffffff" stroke-width="2"/>` +
    `<circle cx="14" cy="14" r="5" fill="#ffffff"/></svg>`;
  return new Icon({ src: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`, anchor: [0.5, 1] });
}

@Component({
  selector: 'app-gis-location-search',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './gis-location-search.component.html',
  styleUrl: './gis-location-search.component.css',
})
export class GisLocationSearchComponent implements OnInit, OnChanges, OnDestroy {
  @Input() map?: Map;
  @Input() lrsRoutes: LrsRoute[] = [];
  @Input() selectedLrsRouteId: number | null = null;
  /** Host lookup for features already in the project; bbox/lon/lat are EPSG:4326. */
  @Input() searchFeatures?: (query: string) => GeoPlace[];
  @Input() topOffset = 64;
  @Output() closed = new EventEmitter<void>();

  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('pinPopup', { static: true }) pinPopup!: ElementRef<HTMLElement>;

  @HostBinding('style.top.px') get hostTop(): number {
    return this.topOffset;
  }

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly geo = inject(GeoSearchService);
  private readonly lrs = inject(LrsService);
  private readonly zone = inject(NgZone);

  mode: 'search' | 'directions' = 'search';
  query = '';
  inputFocused = false;
  searching = false;
  searchError = '';
  searched = false;
  groups: GeoResultGroup[] = [];
  selected: GeoPlace | null = null;

  saved: GeoPlace[] = [];
  recent: GeoPlace[] = [];
  editingSavedId: string | null = null;
  editingLabel = '';

  profile: GeoRouteProfile = 'car';
  readonly profiles: Array<{ id: GeoRouteProfile; icon: string; label: string }> = [
    { id: 'car', icon: '🚗', label: 'Car' },
    { id: 'bike', icon: '🚲', label: 'Bicycle' },
    { id: 'foot', icon: '🚶', label: 'Walk' },
  ];
  endpoints: Record<Endpoint, EndpointState> = {
    from: { text: '', place: null, groups: [], loading: false, error: '' },
    to: { text: '', place: null, groups: [], loading: false, error: '' },
  };
  readonly endpointKeys: Endpoint[] = ['from', 'to'];
  activeEndpoint: Endpoint | null = null;
  picking: Endpoint | null = null;
  routing = false;
  routeError = '';
  route: GeoRoute | null = null;
  activeStep = -1;
  copied = false;

  private overlaySource = new VectorSource();
  private overlayLayer = new OverlayVectorLayer({
    source: this.overlaySource,
    zIndex: 10000,
    style: (f) => this.styleFor(f as Feature),
    properties: { gisTransient: true, gisOverlay: true, name: 'Location search' },
  });
  private popup?: Overlay;
  private translate?: Translate;
  private attachedMap?: Map;
  private mapKeys: EventsKey[] = [];
  private pickKey?: EventsKey;
  private suspendedInteractions: Array<[Interaction, boolean]> = [];
  private reorderingInteractions = false;
  private fitNextRoute = true;

  private readonly search$ = new Subject<string>();
  private readonly endpointSearch$: Record<Endpoint, Subject<string>> = { from: new Subject(), to: new Subject() };
  private readonly route$ = new Subject<void>();
  private readonly subs = new Subscription();
  private readonly pinStyles = {
    pin: new Style({ image: pinIcon(PIN_COLORS.pin) }),
    from: new Style({ image: pinIcon(PIN_COLORS.from) }),
    to: new Style({ image: pinIcon(PIN_COLORS.to) }),
  };
  private readonly routeStyles = [
    new Style({ stroke: new Stroke({ color: 'rgba(255,255,255,0.95)', width: 9 }) }),
    new Style({ stroke: new Stroke({ color: '#2563eb', width: 5 }) }),
  ];
  private readonly stepStyle = new Style({
    image: new CircleStyle({ radius: 7, fill: new Fill({ color: '#ffffff' }), stroke: new Stroke({ color: '#2563eb', width: 3 }) }),
  });

  ngOnInit(): void {
    this.saved = this.geo.loadPlaces('saved');
    this.recent = this.geo.loadPlaces('recent');

    this.subs.add(
      this.search$
        .pipe(switchMap((q) => this.resolve$(q)))
        .subscribe(({ groups, error }) => {
          this.searching = false;
          this.searched = true;
          this.groups = groups;
          this.searchError = error;
        })
    );
    (['from', 'to'] as Endpoint[]).forEach((which) => {
      this.subs.add(
        this.endpointSearch$[which]
          .pipe(switchMap((q) => this.resolve$(q)))
          .subscribe(({ groups, error }) => {
            const ep = this.endpoints[which];
            ep.loading = false;
            ep.groups = groups;
            ep.error = error || (groups.length ? '' : 'No results found');
          })
      );
    });
    this.subs.add(
      this.route$
        .pipe(
          switchMap(() => {
            const from = this.endpoints.from.place;
            const to = this.endpoints.to.place;
            if (!from || !to) return of(null);
            this.routing = true;
            this.routeError = '';
            return this.geo.route(this.profile, from, to).pipe(
              catchError((err) => {
                this.routeError = this.describeRouteError(err);
                return of(null);
              })
            );
          })
        )
        .subscribe((route) => {
          this.routing = false;
          this.route = route;
          this.activeStep = -1;
          this.drawRoute();
        })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['map'] && this.map && this.map !== this.attachedMap) {
      this.detachMap();
      this.attachMap(this.map);
    }
  }

  ngOnDestroy(): void {
    this.cancelPick();
    this.detachMap();
    this.subs.unsubscribe();
  }

  /** Focus the search box (Ctrl/Cmd+K from the host). */
  focus(): void {
    setTimeout(() => {
      this.searchInput?.nativeElement.focus();
      this.searchInput?.nativeElement.select();
    });
  }

  // ---------------------------------------------------------------- map wiring

  private attachMap(m: Map): void {
    this.attachedMap = m;
    m.addLayer(this.overlayLayer);

    this.popup = new Overlay({
      element: this.pinPopup.nativeElement,
      positioning: 'bottom-center',
      offset: [0, -46],
      stopEvent: true,
    });
    m.addOverlay(this.popup);

    this.translate = new Translate({
      layers: [this.overlayLayer],
      filter: (f) => f.get('role') === 'from' || f.get('role') === 'to',
    });
    this.translate.on('translateend', (e) => {
      this.zone.run(() => {
        const f = e.features.item(0) as Feature<Point> | undefined;
        const which = f?.get('role') as Endpoint | undefined;
        const coord = f?.getGeometry()?.getCoordinates();
        if (!which || !coord) return;
        const [lon, lat] = toLonLat(coord);
        this.fitNextRoute = false;
        this.setPickedEndpoint(which, lon, lat);
      });
    });
    m.addInteraction(this.translate);

    const interactions = m.getInteractions();
    this.mapKeys.push(interactions.on('add', (e) => this.keepTranslateOnTop(e as CollectionEvent<Interaction>)));
  }

  private detachMap(): void {
    const m = this.attachedMap;
    if (!m) return;
    unByKey(this.mapKeys);
    this.mapKeys = [];
    if (this.translate) m.removeInteraction(this.translate);
    if (this.popup) m.removeOverlay(this.popup);
    m.removeLayer(this.overlayLayer);
    this.translate = undefined;
    this.popup = undefined;
    this.attachedMap = undefined;
  }

  /** Interactions added later run first; keep marker dragging ahead of the editor's tools. */
  private keepTranslateOnTop(e: CollectionEvent<Interaction>): void {
    if (this.reorderingInteractions || e.element === this.translate) return;
    queueMicrotask(() => {
      const coll = this.attachedMap?.getInteractions();
      if (!coll || !this.translate) return;
      this.reorderingInteractions = true;
      coll.remove(this.translate);
      coll.push(this.translate);
      this.reorderingInteractions = false;
    });
  }

  private styleFor(f: Feature): Style | Style[] | undefined {
    const role = f.get('role');
    if (role === 'route') return this.routeStyles;
    if (role === 'step') return this.stepStyle;
    if (role === 'pin' || role === 'from' || role === 'to') return this.pinStyles[role as 'pin' | 'from' | 'to'];
    return undefined;
  }

  private setMarker(role: 'pin' | 'from' | 'to' | 'step', place: { lon: number; lat: number } | null): void {
    this.overlaySource
      .getFeatures()
      .filter((f) => f.get('role') === role)
      .forEach((f) => this.overlaySource.removeFeature(f));
    if (!place) return;
    const f = new Feature({ geometry: new Point(fromLonLat([place.lon, place.lat])) });
    f.set('role', role);
    if (role === 'from' || role === 'to') f.set('gisOverlay', true);
    this.overlaySource.addFeature(f);
  }

  private viewbox(): [number, number, number, number] | undefined {
    const m = this.attachedMap;
    const size = m?.getSize();
    if (!m || !size) return undefined;
    const e = transformExtent(m.getView().calculateExtent(size), m.getView().getProjection(), 'EPSG:4326');
    const clamp = (v: number, lim: number) => Math.max(-lim, Math.min(lim, v));
    return [clamp(e[0], 180), clamp(e[1], 85), clamp(e[2], 180), clamp(e[3], 85)];
  }

  /** Map padding [top, right, bottom, left] that keeps fitted content clear of this panel and the pin popup. */
  private viewPadding(forPopup: boolean): number[] {
    const m = this.attachedMap;
    const mapRect = m?.getViewport().getBoundingClientRect();
    const panelRect = this.host.nativeElement.querySelector('.gls')?.getBoundingClientRect();
    const popupRoom = forPopup ? 190 : 40;
    if (!mapRect || !panelRect || !panelRect.width) return [popupRoom, 40, 40, 40];
    const besidePanel = mapRect.right - panelRect.right;
    if (besidePanel >= 320) {
      return [Math.max(popupRoom, this.topOffset), 40, 40, panelRect.right - mapRect.left + 24];
    }
    return [Math.max(popupRoom, panelRect.bottom - mapRect.top + 24), 24, 24, 24];
  }

  private flyTo(place: GeoPlace): void {
    const m = this.attachedMap;
    if (!m) return;
    const view = m.getView();
    const bb = place.bbox;
    const tiny = !bb || Math.abs(bb[2] - bb[0]) < 0.0005 || Math.abs(bb[3] - bb[1]) < 0.0005;
    const padding = this.viewPadding(true);
    if (!tiny && bb) {
      view.fit(transformExtent(bb, 'EPSG:4326', view.getProjection()), { padding, maxZoom: 18, duration: 600 });
    } else {
      view.fit(new Point(fromLonLat([place.lon, place.lat])), {
        padding,
        maxZoom: Math.max(view.getZoom() || 0, 16),
        duration: 600,
      });
    }
  }

  // ---------------------------------------------------------------- query resolution

  private resolve$(query: string): Observable<{ groups: GeoResultGroup[]; error: string }> {
    const q = query.trim();
    if (!q) return of({ groups: [], error: '' });

    const coords = this.geo.parseCoordinates(q);
    const features = this.safeFeatureSearch(q);
    const chainage$ = this.chainage$(q);
    const places$: Observable<{ items: GeoPlace[]; error: string }> = coords.length
      ? of({ items: [], error: '' })
      : this.geo.search(q, this.viewbox()).pipe(
          map((items) => ({ items, error: '' })),
          catchError((err) => of({ items: [] as GeoPlace[], error: this.describeSearchError(err) }))
        );

    return forkJoin([chainage$, places$]).pipe(
      map(([chain, places]) => {
        const groups: GeoResultGroup[] = [];
        if (coords.length) groups.push({ title: 'Coordinates', items: coords });
        if (chain.items.length) groups.push({ title: 'Chainage', items: chain.items });
        if (features.length) groups.push({ title: 'In this project', items: features });
        if (places.items.length) groups.push({ title: 'Places', items: places.items });
        const error = chain.error || (groups.length ? '' : places.error);
        return { groups, error };
      })
    );
  }

  private safeFeatureSearch(q: string): GeoPlace[] {
    try {
      return (this.searchFeatures?.(q) || []).slice(0, 10);
    } catch {
      return [];
    }
  }

  private chainage$(q: string): Observable<{ items: GeoPlace[]; error: string }> {
    const parsed = this.geo.parseChainage(q);
    if (!parsed) return of({ items: [], error: '' });
    const eq = (a: string | undefined, b: string) => String(a || '').trim().toLowerCase() === b.toLowerCase();
    const route = parsed.routeCode
      ? this.lrsRoutes.find((r) => eq(r.route_code, parsed.routeCode!) || eq(r.route_name, parsed.routeCode!))
      : this.lrsRoutes.find((r) => r.id === this.selectedLrsRouteId) || this.lrsRoutes[0];
    if (!route) {
      return of({
        items: [],
        error: parsed.routeCode ? `No LRS route named "${parsed.routeCode}" in this project` : 'Load an LRS route to search by chainage',
      });
    }
    return this.lrs.geometryAtOffset(route.id, parsed.measureKm, 0).pipe(
      map((res) => {
        const c = res?.data?.center_point?.coordinates;
        if (!c) return { items: [], error: 'That chainage is not on the route' };
        const place: GeoPlace = {
          id: `chainage:${route.id}:${parsed.measureKm}`,
          kind: 'chainage',
          label: `${route.route_code} ${res.data.chainage_label || `km ${parsed.measureKm}`}`,
          sublabel: route.route_name,
          lon: c[0],
          lat: c[1],
        };
        return { items: [place], error: '' };
      }),
      catchError((err) => of({ items: [] as GeoPlace[], error: err?.error?.error || 'That chainage is not on the route' }))
    );
  }

  private describeSearchError(err: { status?: number }): string {
    if (err?.status === 429) return 'Too many searches. Wait a moment and try again.';
    if (err?.status === 0) return 'Place search is unreachable. Check your connection.';
    return 'Place search failed. Try again.';
  }

  private describeRouteError(err: { code?: string; status?: number; message?: string }): string {
    if (err?.code === 'NoRoute') return 'No route found between these points.';
    if (err?.code === 'NoSegment') return 'One of the points is too far from a road.';
    if (err?.status === 429) return 'Routing service is busy. Try again shortly.';
    if (err?.status === 0) return 'Routing service is unreachable. Check your connection.';
    return err?.message || 'Could not calculate a route.';
  }

  // ---------------------------------------------------------------- search view

  runSearch(): void {
    const q = this.query.trim();
    if (!q) return;
    this.searching = true;
    this.searchError = '';
    this.groups = [];
    this.search$.next(q);
  }

  clearQuery(): void {
    this.query = '';
    this.groups = [];
    this.searched = false;
    this.searchError = '';
    this.searchInput?.nativeElement.focus();
  }

  whereIsThis(): void {
    const m = this.attachedMap;
    const center = m?.getView().getCenter();
    if (!center) return;
    const [lon, lat] = toLonLat(center, m!.getView().getProjection());
    this.searching = true;
    this.searchError = '';
    this.subs.add(
      this.geo.reverse(lon, lat).subscribe({
        next: (place) => {
          this.searching = false;
          this.searched = true;
          this.query = place.label;
          this.groups = [{ title: 'Map centre', items: [{ ...place, kind: 'place' }] }];
        },
        error: (err) => {
          this.searching = false;
          this.searchError = this.describeSearchError(err);
        },
      })
    );
  }

  select(place: GeoPlace): void {
    this.selected = place;
    this.setMarker('pin', place);
    this.flyTo(place);
    this.popup?.setPosition(fromLonLat([place.lon, place.lat]));
    this.copied = false;
    this.remember(place);
  }

  dismissPin(): void {
    this.selected = null;
    this.setMarker('pin', null);
    this.popup?.setPosition(undefined);
  }

  get showQuickLists(): boolean {
    return !this.query.trim() && !this.groups.length && (this.saved.length > 0 || this.recent.length > 0);
  }

  isSaved(place: GeoPlace | null): boolean {
    return !!place && this.saved.some((p) => this.samePlace(p, place));
  }

  toggleSaved(place: GeoPlace | null): void {
    if (!place) return;
    if (this.isSaved(place)) {
      this.saved = this.saved.filter((p) => !this.samePlace(p, place));
    } else {
      const { ref: _ref, ...plain } = place;
      this.saved = [{ ...plain, id: `saved:${Date.now()}`, kind: 'saved' }, ...this.saved];
    }
    this.geo.storePlaces('saved', this.saved);
  }

  removeSaved(place: GeoPlace): void {
    this.saved = this.saved.filter((p) => p.id !== place.id);
    this.geo.storePlaces('saved', this.saved);
  }

  startRename(place: GeoPlace): void {
    this.editingSavedId = place.id;
    this.editingLabel = place.label;
  }

  commitRename(): void {
    const label = this.editingLabel.trim();
    if (this.editingSavedId && label) {
      this.saved = this.saved.map((p) => (p.id === this.editingSavedId ? { ...p, label } : p));
      this.geo.storePlaces('saved', this.saved);
    }
    this.editingSavedId = null;
  }

  clearRecent(): void {
    this.recent = [];
    this.geo.storePlaces('recent', this.recent);
  }

  copyCoordinates(place: GeoPlace | null): void {
    if (!place) return;
    const text = `${place.lat.toFixed(6)}, ${place.lon.toFixed(6)}`;
    navigator.clipboard?.writeText(text).then(
      () => this.zone.run(() => (this.copied = true)),
      () => undefined
    );
  }

  private remember(place: GeoPlace): void {
    const { ref: _ref, ...plain } = place;
    const entry: GeoPlace = { ...plain, kind: 'recent', id: `recent:${plain.id}` };
    this.recent = [entry, ...this.recent.filter((p) => !this.samePlace(p, place))].slice(0, 10);
    this.geo.storePlaces('recent', this.recent);
  }

  private samePlace(a: GeoPlace, b: GeoPlace): boolean {
    return Math.abs(a.lon - b.lon) < 1e-6 && Math.abs(a.lat - b.lat) < 1e-6;
  }

  // ---------------------------------------------------------------- directions view

  openDirections(from?: GeoPlace | null, to?: GeoPlace | null): void {
    this.mode = 'directions';
    this.popup?.setPosition(undefined);
    this.setMarker('pin', null);
    if (from !== undefined) this.applyEndpoint('from', from);
    if (to !== undefined) this.applyEndpoint('to', to);
    if (from === undefined && to === undefined && this.selected && !this.endpoints.to.place) {
      this.applyEndpoint('to', this.selected);
    }
    this.fitNextRoute = true;
    this.route$.next();
  }

  backToSearch(): void {
    this.cancelPick();
    this.mode = 'search';
    this.clearDirections();
    if (this.selected) {
      this.setMarker('pin', this.selected);
      this.popup?.setPosition(fromLonLat([this.selected.lon, this.selected.lat]));
    }
  }

  setProfile(p: GeoRouteProfile): void {
    if (this.profile === p) return;
    this.profile = p;
    this.fitNextRoute = false;
    this.route$.next();
  }

  searchEndpoint(which: Endpoint): void {
    const ep = this.endpoints[which];
    const q = ep.text.trim();
    if (!q) return;
    this.activeEndpoint = which;
    ep.loading = true;
    ep.error = '';
    ep.groups = [];
    this.endpointSearch$[which].next(q);
  }

  onEndpointEdited(which: Endpoint): void {
    const ep = this.endpoints[which];
    if (ep.place && ep.text !== ep.place.label) {
      ep.place = null;
      this.setMarker(which, null);
      this.route = null;
      this.drawRoute();
    }
  }

  chooseEndpoint(which: Endpoint, place: GeoPlace): void {
    this.applyEndpoint(which, place);
    this.remember(place);
    this.fitNextRoute = true;
    this.route$.next();
  }

  swap(): void {
    const { from, to } = this.endpoints;
    this.endpoints = { from: to, to: from };
    this.setMarker('from', this.endpoints.from.place);
    this.setMarker('to', this.endpoints.to.place);
    this.fitNextRoute = false;
    this.route$.next();
  }

  useMyLocation(which: Endpoint): void {
    const ep = this.endpoints[which];
    if (!navigator.geolocation) {
      ep.error = 'Location is not available in this browser';
      return;
    }
    ep.loading = true;
    ep.error = '';
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        this.zone.run(() => {
          ep.loading = false;
          const { longitude: lon, latitude: lat } = pos.coords;
          this.applyEndpoint(which, { id: `me:${lon},${lat}`, kind: 'picked', label: 'My location', lon, lat });
          this.fitNextRoute = true;
          this.route$.next();
        }),
      (err) =>
        this.zone.run(() => {
          ep.loading = false;
          ep.error = err.code === err.PERMISSION_DENIED ? 'Location permission was denied' : 'Could not get your location';
        }),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  }

  startPick(which: Endpoint): void {
    const m = this.attachedMap;
    if (!m) return;
    this.cancelPick();
    this.picking = which;
    this.suspendedInteractions = m
      .getInteractions()
      .getArray()
      .filter((i) => i !== this.translate && !NAV_INTERACTIONS.some((T) => i instanceof T))
      .map((i) => [i, i.getActive()] as [Interaction, boolean]);
    this.suspendedInteractions.forEach(([i]) => i.setActive(false));
    m.getViewport().classList.add('gis-location-search--picking');
    this.pickKey = m.once('singleclick', (evt) => {
      this.zone.run(() => {
        const target = this.picking;
        this.cancelPick();
        if (!target) return;
        const [lon, lat] = toLonLat(evt.coordinate, m.getView().getProjection());
        this.fitNextRoute = true;
        this.setPickedEndpoint(target, lon, lat);
      });
    }) as EventsKey;
  }

  cancelPick(): void {
    if (this.pickKey) unByKey(this.pickKey);
    this.pickKey = undefined;
    this.suspendedInteractions.forEach(([i, wasActive]) => i.setActive(wasActive));
    this.suspendedInteractions = [];
    this.attachedMap?.getViewport().classList.remove('gis-location-search--picking');
    this.picking = null;
  }

  focusStep(index: number): void {
    const step = this.route?.steps[index];
    if (!step || !this.attachedMap) return;
    this.activeStep = index;
    this.setMarker('step', step);
    const view = this.attachedMap.getView();
    view.animate({ center: fromLonLat([step.lon, step.lat]), zoom: Math.max(view.getZoom() || 0, 17), duration: 400 });
  }

  clearDirections(): void {
    this.endpoints = {
      from: { text: '', place: null, groups: [], loading: false, error: '' },
      to: { text: '', place: null, groups: [], loading: false, error: '' },
    };
    this.route = null;
    this.routeError = '';
    this.activeEndpoint = null;
    ['from', 'to', 'route', 'step'].forEach((role) =>
      this.overlaySource.getFeatures().filter((f) => f.get('role') === role).forEach((f) => this.overlaySource.removeFeature(f))
    );
  }

  private applyEndpoint(which: Endpoint, place: GeoPlace | null): void {
    this.endpoints[which] = {
      text: place?.label || '',
      place,
      groups: [],
      loading: false,
      error: '',
    };
    if (this.activeEndpoint === which) this.activeEndpoint = null;
    this.setMarker(which, place);
  }

  /** Map click or marker drag: place the point now, then replace the label with a reverse-geocoded one. */
  private setPickedEndpoint(which: Endpoint, lon: number, lat: number): void {
    const label = `${lat.toFixed(5)}, ${lon.toFixed(5)}`;
    const place: GeoPlace = { id: `picked:${label}`, kind: 'picked', label, lon, lat };
    this.applyEndpoint(which, place);
    this.route$.next();
    this.subs.add(
      this.geo.reverse(lon, lat).subscribe({
        next: (named) => {
          const ep = this.endpoints[which];
          if (ep.place !== place) return;
          const updated = { ...place, label: named.label, sublabel: named.sublabel };
          ep.place = updated;
          ep.text = updated.label;
        },
        error: () => undefined,
      })
    );
  }

  private drawRoute(): void {
    this.overlaySource
      .getFeatures()
      .filter((f) => f.get('role') === 'route' || f.get('role') === 'step')
      .forEach((f) => this.overlaySource.removeFeature(f));
    const m = this.attachedMap;
    if (!this.route || !m) return;
    const geom = new GeoJSON().readGeometry(this.route.geometry, {
      dataProjection: 'EPSG:4326',
      featureProjection: m.getView().getProjection(),
    }) as LineString;
    const f = new Feature({ geometry: geom });
    f.set('role', 'route');
    this.overlaySource.addFeature(f);
    if (this.fitNextRoute) {
      m.getView().fit(geom.getExtent(), { padding: this.viewPadding(false), maxZoom: 17, duration: 600 });
    }
    this.fitNextRoute = true;
  }

  // ---------------------------------------------------------------- shared

  onKeydown(event: KeyboardEvent): void {
    event.stopPropagation();
    if (event.key !== 'Escape') return;
    if (this.picking) this.cancelPick();
    else if (this.editingSavedId) this.editingSavedId = null;
    else if (this.activeEndpoint) this.activeEndpoint = null;
    else if (this.groups.length || this.searchError) this.clearQuery();
    else this.close();
  }

  close(): void {
    this.cancelPick();
    this.clearDirections();
    this.dismissPin();
    this.mode = 'search';
    this.closed.emit();
  }

  formatDistance(m: number): string {
    if (!Number.isFinite(m)) return '';
    if (m < 1000) return `${Math.round(m / 10) * 10} m`;
    return `${(m / 1000).toFixed(m < 10000 ? 1 : 0)} km`;
  }

  formatDuration(s: number): string {
    if (!Number.isFinite(s)) return '';
    const mins = Math.max(1, Math.round(s / 60));
    if (mins < 60) return `${mins} min`;
    const h = Math.floor(mins / 60);
    const rem = mins % 60;
    return rem ? `${h} h ${rem} min` : `${h} h`;
  }

  kindIcon(place: GeoPlace): string {
    switch (place.kind) {
      case 'coords':
        return '⌖';
      case 'chainage':
        return '📏';
      case 'feature':
        return '◆';
      case 'saved':
        return '★';
      case 'recent':
        return '🕘';
      default:
        return '📍';
    }
  }

  trackPlace = (_: number, p: GeoPlace) => p.id;
}
